import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import { Form, Table } from "react-bootstrap";
import { fetchAllDataAPI, filterByParams } from "../../utils/api";
import Skeleton from "react-loading-skeleton";
import { numberFormat } from "../../utils/numberFormat";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import ReportNavBar from "../../components/ReportNavBar";
import withAuth from "../../utils/withAuth";
import { CiExport } from "react-icons/ci";
import CsvDownloader from "react-csv-downloader";
import { Link } from "react-router-dom";

const CenterPerformance = () => {
    const [report, setReport] = useState([]);
    const [filteredReport, setFilteredReport] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // default = today..today (kept from your code)
    const [dateRange, setDateRange] = useState({
        startDate: new Date().toLocaleDateString("en-US"),
        endDate: new Date().toLocaleDateString("en-US"),
    });

    // --- Compare day-window across months ---
    const [compareMode, setCompareMode] = useState(false);
    const [compare, setCompare] = useState({
        startDay: 1,
        endDay: 10,
        monthA: moment().subtract(1, "month").format("YYYY-MM"),
        monthB: moment().format("YYYY-MM"),
    });

    // helpers for compare window
    const clampToMonth = (ym, day) => {
        const m = moment(ym + "-01");
        const max = m.daysInMonth();
        const d = Math.min(Math.max(1, parseInt(day || 1, 10)), max);
        return m.date(d);
    };

    const makeRangeFromWindow = (ym, startDay, endDay) => {
        const start = clampToMonth(ym, startDay).startOf("day");
        const end = clampToMonth(ym, endDay).endOf("day");
        return start.isAfter(end) ? { from: end, to: start } : { from: start, to: end };
    };

    const labelForWindow = (ym, startDay, endDay) => {
        const from = clampToMonth(ym, startDay);
        const to = clampToMonth(ym, endDay);
        const a = from.isAfter(to) ? to : from;
        const b = from.isAfter(to) ? from : to;
        return `${a.format("MMM")} ${a.format("DD")}–${b.format("DD")}`;
    };

    // derived labels for headers/CSV when compareMode is on
    const compareLabels = useMemo(() => {
        if (!compareMode) return { left: null, right: null };
        return {
            left: labelForWindow(compare.monthA, compare.startDay, compare.endDay),
            right: labelForWindow(compare.monthB, compare.startDay, compare.endDay),
        };
    }, [compareMode, compare]);

    useEffect(() => {
        getCenterPerformanceReport();
    }, []);

    const getCenterPerformanceReport = async () => {
        setIsLoading(true);
        try {
            const res = await fetchAllDataAPI(
                "super-admin-get-cluster-head-performance-report-last-two-months"
            );
            if (res.data.status === 1) {
                setReport(res.data.report);
                setFilteredReport(res.data.report);
            }
        } catch (error) {
            console.error("Error fetching report:", error);
        }
        setIsLoading(false);
    };

    const handleFilterByDate = async (event, picker) => {
        setIsLoading(true);
        const selectedRange = {
            startDate: picker.startDate.format("YYYY-MM-DD"),
            endDate: picker.endDate.format("YYYY-MM-DD"),
        };
        setDateRange(selectedRange);
        try {
            const res = await filterByParams(
                "super-admin-get-filter-cluster-head-performance-report-last-two-months",
                selectedRange
            );
            if (res.data.status === 1) {
                setReport(res.data.report);
                setFilteredReport(res.data.report);
            }
        } catch (err) {
            console.error(err);
        }
        setIsLoading(false);
    };

    const handleReset = async () => {
        setSearchTerm("");
        setCompareMode(false);
        const defaultRange = {
            startDate: moment().subtract(2, "months").startOf("month").toDate(),
            endDate: moment().subtract(1, "months").endOf("month").toDate(),
        };
        setDateRange(defaultRange);
        setIsLoading(true);
        try {
            const res = await fetchAllDataAPI(
                "super-admin-get-filter-cluster-head-performance-report-last-two-months"
            );
            if (res.data.status === 1) {
                setReport(res.data.report);
                setFilteredReport(res.data.report);
            }
        } catch (error) {
            console.error("Reset fetch error:", error);
        }
        setIsLoading(false);
    };

    // apply compare day-window across two months
    const handleApplyCompare = async () => {
        setIsLoading(true);
        const r1 = makeRangeFromWindow(compare.monthA, compare.startDay, compare.endDay);
        const r2 = makeRangeFromWindow(compare.monthB, compare.startDay, compare.endDay);

        const payload = {
            from1: r1.from.format("YYYY-MM-DD"),
            to1: r1.to.format("YYYY-MM-DD"),
            from2: r2.from.format("YYYY-MM-DD"),
            to2: r2.to.format("YYYY-MM-DD"),
        };

        try {
            const res = await filterByParams(
                "super-admin-get-cluster-head-performance-report-compare",
                payload
            );
            if (res.data.status === 1) {
                setReport(res.data.report);
                setFilteredReport(res.data.report);
            }
        } catch (e) {
            console.error("Compare fetch error:", e);
        }
        setIsLoading(false);
    };

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        const filteredData = report.filter((item) =>
            item.cluster_head_name?.toLowerCase().includes(value)
        );
        setFilteredReport(filteredData);
    };

    // CSV columns — dynamic month names or compare labels
    const leftHeader =
        compareMode && compareLabels.left
            ? compareLabels.left
            : filteredReport[0]?.last_month_name || "Month 1";
    const rightHeader =
        compareMode && compareLabels.right
            ? compareLabels.right
            : filteredReport[0]?.current_month_name || "Month 2";

    // Base CSV column config
    const baseColumns = [
        { id: "s_no", displayName: "Serial No" },
        { id: "cluster_head_name", displayName: "Cluster Head Name" },
        { id: "month_1_value", displayName: leftHeader },
        { id: "month_2_value", displayName: rightHeader },
        { id: "growth_percentage", displayName: "Growth (%)" },
        { id: "yesterday_total", displayName: "Yesterday Total Order" },
        // Delivered columns will be added conditionally below
        { id: "block_01_10_delivery_percentage", displayName: "01–10 (%)" },
        { id: "block_11_20_delivery_percentage", displayName: "11–20 (%)" },
        { id: "block_21_till_delivery_percentage", displayName: "21–31 (%)" },
        { id: "ticket_size", displayName: "Ticket Size" },
    ];

    const columns = useMemo(() => {
        if (compareMode) {
            // add delivery compare columns
            return [
                ...baseColumns.slice(0, 6),
                { id: "delivery_percentage_left", displayName: `Delivery % (${leftHeader})` },
                { id: "delivery_percentage_right", displayName: `Delivery % (${rightHeader})` },
                { id: "delivery_percentage_change", displayName: "Δ Delivery % (pp)" },
                ...baseColumns.slice(6),
            ];
        }
        // non-compare: single Delivered(%)
        return [
            ...baseColumns.slice(0, 6),
            { id: "delivery_percentage", displayName: "Delivered (%)" },
            ...baseColumns.slice(6),
        ];
    }, [compareMode, leftHeader, rightHeader]);

    const sortedReport = [...filteredReport].sort((a, b) => {
        if (a.cluster_head_name?.toLowerCase() === "grand_total") return 1;
        if (b.cluster_head_name?.toLowerCase() === "grand_total") return -1;
        return a.cluster_head_name.localeCompare(b.cluster_head_name);
    });

    const exportReport = useMemo(
        () =>
            sortedReport.map((item, index) => ({
                s_no: index + 1,
                cluster_head_name: item.cluster_head_name,
                month_1_value: item.last_month_total || 0,
                month_2_value: item.current_month_total || 0,
                growth_percentage:
                    item.growth_percentage !== null
                        ? Number(item.growth_percentage).toFixed(2)
                        : 0,
                yesterday_total: item.yesterday_total || 0,

                // delivery fields (conditionally used by columns)
                delivery_percentage:
                    item.delivery_percentage !== null
                        ? Number(item.delivery_percentage).toFixed(2)
                        : 0,
                delivery_percentage_left:
                    item.delivery_percentage_left !== null &&
                        item.delivery_percentage_left !== undefined
                        ? Number(item.delivery_percentage_left).toFixed(2)
                        : 0,
                delivery_percentage_right:
                    item.delivery_percentage_right !== null &&
                        item.delivery_percentage_right !== undefined
                        ? Number(item.delivery_percentage_right).toFixed(2)
                        : 0,
                delivery_percentage_change:
                    item.delivery_percentage_change !== null &&
                        item.delivery_percentage_change !== undefined
                        ? Number(item.delivery_percentage_change).toFixed(2)
                        : 0,

                block_01_10_delivery_percentage:
                    item.block_01_10_delivery_percentage !== null
                        ? Number(item.block_01_10_delivery_percentage).toFixed(2)
                        : 0,
                block_11_20_delivery_percentage:
                    item.block_11_20_delivery_percentage !== null
                        ? Number(item.block_11_20_delivery_percentage).toFixed(2)
                        : 0,
                block_21_till_delivery_percentage:
                    item.block_21_till_delivery_percentage !== null
                        ? Number(item.block_21_till_delivery_percentage).toFixed(2)
                        : 0,
                ticket_size: item.ticket_size || 0,
            })),
        [sortedReport]
    );

    const grandTotal = filteredReport.find(
        (item) => item.cluster_head_name?.toLowerCase() === "grand_total"
    );

    const blockHeaderMonth = useMemo(() => {
        if (compareMode && compare?.monthB) {
            const m = moment(compare.monthB + "-01");
            return {
                mmm: m.format("MMM"),
                lastDay: m.endOf("month").format("DD"),
            };
        }
        const m = moment(dateRange?.endDate);
        return {
            mmm: m.format("MMM"),
            lastDay: m.endOf("month").format("DD"),
        };
    }, [compareMode, compare?.monthB, dateRange?.endDate]);

    const handleCompareToggle = async (e) => {
        const checked = e.target.checked;
        setCompareMode(checked);

        if (checked) {
            // When turning ON -> call compare API with current compare state
            await handleApplyCompare();
        } else {
            // When turning OFF -> reload default non-compare data
            await getCenterPerformanceReport();
        }
    };
    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR: CLUSTER HEAD PERFORMANCE</title>
            </Helmet>
            <ContentWrapper>
                <ReportNavBar />

                <div className="card p-2" style={{ marginBottom: "50px" }}>
                    <div className="heading d-flex justify-content-between mb-2 gap-2 flex-wrap">
                        <h5 className="mb-0 text-uppercase mt-2">
                            Cluster Head Performance Report{" "}
                            {!compareMode ? (
                                <>
                                    from {filteredReport[0]?.last_month_name || "Last Month"} to{" "}
                                    {filteredReport[0]?.current_month_name || "This Month"}
                                </>
                            ) : (
                                <>
                                    ({compareLabels.left}) vs ({compareLabels.right})
                                </>
                            )}
                        </h5>

                        <div className="mt-1" style={{ minWidth: 260 }}>
                            <Form.Control
                                type="text"
                                placeholder="Search by Cluster Head Name"
                                value={searchTerm}
                                onChange={handleSearch}
                                className="mb-3"
                            />
                        </div>

                        <div className="d-flex align-items-start gap-2 flex-wrap">
                            <CsvDownloader
                                filename={`cluster_head_Performance_${moment().format("YYYY_MM_DD")}`}
                                extension=".csv"
                                datas={exportReport}
                                columns={columns}
                                className="btnorderfilter"
                                style={{ marginTop: "7px" }}
                            >
                                <CiExport className="svgexport" />
                                <span
                                    style={{ color: "#fff", border: "none" }}
                                    className="btn btn-info btn-sm btnfilter"
                                >
                                    Export
                                </span>
                            </CsvDownloader>

                            <span onClick={handleReset} className="btnreset">
                                Reset
                            </span>

                            {/* Original date-range picker (kept) */}
                            <div className="btnpicker">
                                <DateRangePicker
                                    initialSettings={{
                                        startDate: dateRange.startDate,
                                        endDate: dateRange.endDate,
                                    }}
                                    onApply={handleFilterByDate}
                                >
                                    <button className="btn btn-md btndaterange">Select Date Range</button>
                                </DateRangePicker>
                            </div>
                        </div>
                    </div>

                    {/* Compare Controls */}
                    <div className="border rounded p-2 mb-3 bg-light">
                        <Form.Check
                            type="switch"
                            id="compare-switch"
                            label="Compare by day-window across months"
                            checked={compareMode}
                            onChange={handleCompareToggle}
                            className="mb-2"
                        />

                        {compareMode && (
                            <div className="d-flex flex-wrap gap-2 align-items-end">
                                <div>
                                    <Form.Label>Start day</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={1}
                                        max={31}
                                        value={compare.startDay}
                                        onChange={(e) =>
                                            setCompare((p) => ({ ...p, startDay: e.target.value }))
                                        }
                                        style={{ width: 110 }}
                                    />
                                </div>
                                <div>
                                    <Form.Label>End day</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={1}
                                        max={31}
                                        value={compare.endDay}
                                        onChange={(e) =>
                                            setCompare((p) => ({ ...p, endDay: e.target.value }))
                                        }
                                        style={{ width: 110 }}
                                    />
                                </div>
                                <div>
                                    <Form.Label>Month A</Form.Label>
                                    <Form.Control
                                        type="month"
                                        value={compare.monthA}
                                        onChange={(e) =>
                                            setCompare((p) => ({ ...p, monthA: e.target.value }))
                                        }
                                        style={{ width: 170 }}
                                    />
                                </div>
                                <div>
                                    <Form.Label>Month B</Form.Label>
                                    <Form.Control
                                        type="month"
                                        value={compare.monthB}
                                        onChange={(e) =>
                                            setCompare((p) => ({ ...p, monthB: e.target.value }))
                                        }
                                        style={{ width: 170 }}
                                    />
                                </div>
                                <div className="ms-auto">
                                    <button className="btn btn-primary" onClick={handleApplyCompare}>
                                        Apply Compare ({compareLabels.left} vs {compareLabels.right})
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="table pagetable table-responsive">
                        <Table className="table-sm table-fixeds" responsive striped bordered hover>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Cluster Head Name</th>
                                    <th>{leftHeader}</th>
                                    <th>{rightHeader}</th>
                                    <th>Growth (%)</th>
                                    <th>Yesterday Total Order</th>

                                    {/* Delivery columns */}
                                    {compareMode ? (
                                        <>
                                            <th>{`Delivery % (${leftHeader})`}</th>
                                            <th>{`Delivery % (${rightHeader})`}</th>
                                            <th>Δ Delivery % (pp)</th>
                                        </>
                                    ) : (
                                        <th>Delivered(%)</th>
                                    )}

                                    <th>01–10 {blockHeaderMonth.mmm}(%)</th>
                                    <th>11–20 {blockHeaderMonth.mmm}(%)</th>
                                    <th>21–{blockHeaderMonth.lastDay} {blockHeaderMonth.mmm}(%)</th>
                                    <th>Ticket Size</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    [...Array(5)].map((_, index) => (
                                        <tr key={index}>
                                            {Array.from({ length: compareMode ? 14 : 12 }).map((_, i) => (
                                                <td key={i}>
                                                    <Skeleton animation="wave" variant="text" />
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : filteredReport.length > 0 ? (
                                    filteredReport
                                        .filter(
                                            (item) => item.cluster_head_name?.toLowerCase() !== "grand_total"
                                        )
                                        .map((item, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <Link
                                                        style={{ letterSpacing: "0.8px", fontWeight: "700" }}
                                                        to={`/center-head-performance-report/${item.center_id}`}
                                                    >
                                                        {item.cluster_head_name}
                                                    </Link>
                                                </td>

                                                <td>{numberFormat(item.last_month_total)}</td>
                                                <td>{numberFormat(item.current_month_total)}</td>

                                                <td
                                                    style={{
                                                        fontWeight: "600",
                                                        color: item.growth_percentage >= 0 ? "green" : "red",
                                                    }}
                                                >
                                                    {item.growth_percentage !== null
                                                        ? Number(item.growth_percentage).toFixed(2) + "%"
                                                        : "N/A"}
                                                </td>

                                                <td>{numberFormat(item.yesterday_total)}</td>

                                                {/* Delivery cells */}
                                                {compareMode ? (
                                                    <>
                                                        <td
                                                            style={{
                                                                color:
                                                                    (item.delivery_percentage_left ?? 0) >= 50
                                                                        ? "white"
                                                                        : "inherit",
                                                                background:
                                                                    (item.delivery_percentage_left ?? 0) >= 50
                                                                        ? "green"
                                                                        : "inherit",
                                                                fontWeight:
                                                                    (item.delivery_percentage_left ?? 0) >= 50
                                                                        ? "bold"
                                                                        : "normal",
                                                            }}
                                                        >
                                                            {item.delivery_percentage_left ?? "N/A"}%
                                                        </td>

                                                        <td
                                                            style={{
                                                                color:
                                                                    (item.delivery_percentage_right ?? 0) >= 50
                                                                        ? "white"
                                                                        : "inherit",
                                                                background:
                                                                    (item.delivery_percentage_right ?? 0) >= 50
                                                                        ? "green"
                                                                        : "inherit",
                                                                fontWeight:
                                                                    (item.delivery_percentage_right ?? 0) >= 50
                                                                        ? "bold"
                                                                        : "normal",
                                                            }}
                                                        >
                                                            {item.delivery_percentage_right ?? "N/A"}%
                                                        </td>

                                                        <td
                                                            style={{
                                                                fontWeight: "600",
                                                                color:
                                                                    (item.delivery_percentage_change ?? 0) >= 0
                                                                        ? "green"
                                                                        : "red",
                                                            }}
                                                        >
                                                            {item.delivery_percentage_change ?? 0}%
                                                        </td>
                                                    </>
                                                ) : (
                                                    <td
                                                        style={{
                                                            color:
                                                                (item.delivery_percentage ?? 0) >= 50
                                                                    ? "white"
                                                                    : "inherit",
                                                            background:
                                                                (item.delivery_percentage ?? 0) >= 50
                                                                    ? "green"
                                                                    : "inherit",
                                                            fontWeight:
                                                                (item.delivery_percentage ?? 0) >= 50
                                                                    ? "bold"
                                                                    : "normal",
                                                        }}
                                                    >
                                                        {item.delivery_percentage ? item.delivery_percentage + "%" : "N/A"}
                                                    </td>
                                                )}

                                                <td
                                                    style={{
                                                        color:
                                                            item.block_01_10_delivery_percentage >= 50
                                                                ? "white"
                                                                : "inherit",
                                                        background:
                                                            item.block_01_10_delivery_percentage >= 50
                                                                ? "green"
                                                                : "inherit",
                                                        fontWeight:
                                                            item.block_01_10_delivery_percentage >= 50
                                                                ? "bold"
                                                                : "normal",
                                                    }}
                                                >
                                                    {item.block_01_10_delivery_percentage
                                                        ? item.block_01_10_delivery_percentage + "%"
                                                        : "N/A"}
                                                </td>
                                                <td
                                                    style={{
                                                        color:
                                                            item.block_11_20_delivery_percentage >= 50
                                                                ? "white"
                                                                : "inherit",
                                                        background:
                                                            item.block_11_20_delivery_percentage >= 50
                                                                ? "green"
                                                                : "inherit",
                                                        fontWeight:
                                                            item.block_11_20_delivery_percentage >= 50
                                                                ? "bold"
                                                                : "normal",
                                                    }}
                                                >
                                                    {item.block_11_20_delivery_percentage
                                                        ? item.block_11_20_delivery_percentage + "%"
                                                        : "N/A"}
                                                </td>
                                                <td
                                                    style={{
                                                        color:
                                                            item.block_21_till_delivery_percentage >= 50
                                                                ? "white"
                                                                : "inherit",
                                                        background:
                                                            item.block_21_till_delivery_percentage >= 50
                                                                ? "green"
                                                                : "inherit",
                                                        fontWeight:
                                                            item.block_21_till_delivery_percentage >= 50
                                                                ? "bold"
                                                                : "normal",
                                                    }}
                                                >
                                                    {item.block_21_till_delivery_percentage
                                                        ? item.block_21_till_delivery_percentage + "%"
                                                        : "N/A"}
                                                </td>
                                                <td>{numberFormat(item.ticket_size)}</td>
                                            </tr>
                                        ))
                                ) : (
                                    <tr>
                                        <td colSpan={compareMode ? 14 : 12} className="text-center">
                                            No data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>

                            <tfoot>
                                {grandTotal && (
                                    <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
                                        <th colSpan={1} style={{ fontWeight: "700", color: "#000" }}>
                                            #
                                        </th>
                                        <th style={{ fontWeight: "700", color: "#000" }}>Grand Total</th>
                                        <th style={{ fontWeight: "700", color: "#000" }}>
                                            {numberFormat(grandTotal.last_month_total)}
                                        </th>
                                        <th style={{ fontWeight: "700", color: "#000" }}>
                                            {numberFormat(grandTotal.current_month_total)}
                                        </th>
                                        <th
                                            style={{
                                                fontWeight: "700",
                                                color:
                                                    grandTotal.growth_percentage >= 0 ? "green" : "red",
                                            }}
                                        >
                                            {grandTotal.growth_percentage !== null
                                                ? Number(grandTotal.growth_percentage).toFixed(2) + "%"
                                                : "N/A"}
                                        </th>
                                        <th style={{ fontWeight: "700", color: "#000" }}>
                                            {numberFormat(grandTotal.yesterday_total)}
                                        </th>

                                        {/* Grand total delivery compare or single */}
                                        {compareMode ? (
                                            <>
                                                <th style={{ fontWeight: "700", color: "#000" }}>
                                                    {grandTotal.delivery_percentage_left ?? "N/A"}%
                                                </th>
                                                <th style={{ fontWeight: "700", color: "#000" }}>
                                                    {grandTotal.delivery_percentage_right ?? "N/A"}%
                                                </th>
                                                <th
                                                    style={{
                                                        fontWeight: "700",
                                                        color:
                                                            (grandTotal.delivery_percentage_change ?? 0) >= 0
                                                                ? "green"
                                                                : "red",
                                                    }}
                                                >
                                                    {grandTotal.delivery_percentage_change ?? 0}%
                                                </th>
                                            </>
                                        ) : (
                                            <th style={{ fontWeight: "700", color: "#000" }}>
                                                {grandTotal.delivery_percentage
                                                    ? grandTotal.delivery_percentage + "%"
                                                    : "N/A"}
                                            </th>
                                        )}

                                        <th style={{ fontWeight: "700", color: "#000" }}>
                                            {grandTotal.block_01_10_delivery_percentage
                                                ? grandTotal.block_01_10_delivery_percentage + "%"
                                                : "N/A"}
                                        </th>
                                        <th style={{ fontWeight: "700", color: "#000" }}>
                                            {grandTotal.block_11_20_delivery_percentage
                                                ? grandTotal.block_11_20_delivery_percentage + "%"
                                                : "N/A"}
                                        </th>
                                        <th style={{ fontWeight: "700", color: "#000" }}>
                                            {grandTotal.block_21_till_delivery_percentage
                                                ? grandTotal.block_21_till_delivery_percentage + "%"
                                                : "N/A"}
                                        </th>
                                        <th style={{ fontWeight: "700", color: "#000" }}>
                                            {numberFormat(grandTotal.ticket_size)}
                                        </th>
                                    </tr>
                                )}
                            </tfoot>
                        </Table>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(CenterPerformance);
